import { useCallback, useEffect, useState } from "react";
import type { Channel, QrVersion } from "@qr-lifecycle/contracts";
import {
  ArrowLeft,
  CalendarClock,
  Check,
  Copy,
  Download,
  ExternalLink,
  History,
  Pencil,
  Power,
  QrCode,
  Smartphone,
  Trash2,
} from "lucide-react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { api, ApiError } from "../api/client";
import { ErrorState, LoadingState, PageHeading } from "../components/States";
import { formatDateTime, getChannelStatus } from "../lib/channel-status";

function messageFor(error: unknown): string {
  return error instanceof ApiError ? error.message : "请求失败，请稍后重试。";
}

export function ChannelDetailPage() {
  const { channelId = "" } = useParams();
  const navigate = useNavigate();
  const [channel, setChannel] = useState<Channel | null>(null);
  const [versions, setVersions] = useState<QrVersion[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actionError, setActionError] = useState<string | null>(null);
  const [copied, setCopied] = useState<"relay" | "image" | null>(null);
  const [updatingEnabled, setUpdatingEnabled] = useState(false);

  const load = useCallback(() => {
    setLoading(true);
    setError(null);
    Promise.all([api.getChannel(channelId), api.listQrVersions(channelId)]).then(
      ([channelResponse, versionsResponse]) => {
        setChannel(channelResponse.channel);
        setVersions(versionsResponse.qrVersions);
        setLoading(false);
      },
      (loadError: unknown) => {
        setError(messageFor(loadError));
        setLoading(false);
      },
    );
  }, [channelId]);

  useEffect(load, [load]);
  const copyUrl = async (value: string, target: "relay" | "image") => {
    await navigator.clipboard.writeText(value);
    setCopied(target);
    window.setTimeout(() => setCopied(null), 1_500);
  };

  const deleteChannel = async () => {
    if (!channel || !window.confirm(`确定永久删除“${channel.name}”？频道和二维码历史都会被删除，且无法恢复。`)) return;
    try {
      await api.deleteChannel(channel.id);
      navigate("/", { replace: true });
    } catch (deleteError) {
      setActionError(messageFor(deleteError));
    }
  };

  const setChannelEnabled = async (enabled: boolean) => {
    if (!channel) return;
    setUpdatingEnabled(true);
    setActionError(null);
    try {
      const response = await api.updateChannel(channel.id, { enabled });
      setChannel(response.channel);
    } catch (updateError) {
      setActionError(messageFor(updateError));
    } finally {
      setUpdatingEnabled(false);
    }
  };

  if (loading) return <div className="page"><LoadingState label="正在读取频道…" /></div>;
  if (error || !channel) return <div className="page"><ErrorState message={error ?? "频道不存在。"} onRetry={load} /></div>;

  const status = getChannelStatus(channel);
  const publicUrl = `${api.getApiOrigin()}/q/${channel.slug}`;
  const relayQrImageUrl = `${publicUrl}/relay.png`;
  const publicImageUrl = `${publicUrl}/image`;

  return (
    <div className="page">
      <Link className="back-link" to="/"><ArrowLeft size={16} /> 返回工作台</Link>
      <PageHeading
        eyebrow="频道详情"
        title={channel.name}
        description="长期分享码适合对外发布；访客扫码后会看到最新的入群码。"
        action={
          <Link className="button button--primary" to={`/channels/${channel.id}/edit`}><Pencil size={16} /> 编辑</Link>
        }
      />

      <section className="detail-grid">
        <article className="current-qr-card relay-qr-card">
          <div className="card-heading">
            <div><p className="eyebrow">发布后无需更换</p><h2>长期分享码</h2></div>
            <QrCode size={22} />
          </div>
          <div className="qr-preview qr-preview--relay">
            <img src={relayQrImageUrl} alt={`${channel.name} 长期分享二维码`} />
          </div>
          <p className="entry-explanation">
            把它打印或发布出去即可。访客扫码时会看到最新的入群码；以后更新入群码，也不用重新替换这个二维码。
          </p>
          <code className="entry-url">{publicUrl}</code>
          <div className="entry-actions">
            <a
              className="button button--secondary button--small"
              download={`${channel.slug}-share-qr.png`}
              href={relayQrImageUrl}
            >
              <Download size={15} /> 下载分享码
            </a>
            <button
              className="button button--secondary button--small"
              type="button"
              onClick={() => void copyUrl(publicUrl, "relay")}
            >
              {copied === "relay" ? <Check size={15} /> : <Copy size={15} />}
              {copied === "relay" ? "已复制" : "复制分享链接"}
            </button>
            <a className="button button--ghost button--small" href={publicUrl} target="_blank" rel="noreferrer">
              预览扫码页面 <ExternalLink size={15} />
            </a>
          </div>
        </article>

        <article className="current-qr-card">
          <div className="card-heading">
            <div><p className="eyebrow">访客扫码后看到</p><h2>当前入群码</h2></div>
            <span className={`status-badge status-badge--${status.tone}`}><i />{status.label}</span>
          </div>
          <div className={`qr-preview${channel.activeQrVersionId ? "" : " qr-preview--empty"}`}>
            {channel.activeQrVersionId ? (
              <img
                src={publicImageUrl}
                alt={`${channel.name} 当前二维码`}
              />
            ) : (
              <><QrCode size={54} /><strong>还没有二维码</strong><span>打开手机 App 保存群码，并在检测结果中确认创建当前版本</span></>
            )}
          </div>
          <dl className="detail-list">
            <div><dt><CalendarClock size={15} /> 到期时间</dt><dd>{formatDateTime(channel.expiresAt)}</dd></div>
            <div><dt><History size={15} /> 版本数量</dt><dd>{versions.length} 个</dd></div>
          </dl>
          <code className="entry-url">{publicImageUrl}</code>
          <div className="entry-actions">
            <button
              className="button button--secondary button--small"
              type="button"
              onClick={() => void copyUrl(publicImageUrl, "image")}
            >
              {copied === "image" ? <Check size={15} /> : <Copy size={15} />}
              {copied === "image" ? "已复制" : "复制图片链接"}
            </button>
            <a className="button button--ghost button--small" href={publicImageUrl} target="_blank" rel="noreferrer">
              查看入群码 <ExternalLink size={15} />
            </a>
          </div>
        </article>

        <article className="upload-card mobile-automation-card">
          <div className="card-heading">
            <div><p className="eyebrow">手机自动维护</p><h2>手机更新入群码，分享码无需更换</h2></div>
            <Smartphone size={22} />
          </div>
          <div className="automation-steps">
            <p><strong>1</strong><span>在微信、小红书或 Discord 中保存新二维码。</span></p>
            <p><strong>2</strong><span>回到 Fallinlife，App 只会把支持平台的群码交给 AI 识别名称和到期时间。</span></p>
            <p><strong>3</strong><span>高置信度结果直接更新；不确定的结果只需点一次确认。</span></p>
          </div>
          <p className="upload-card__hint"><Smartphone size={14} /> Web 仅用于查看、纠错和运维，不会读取或分析你的图片。</p>
          <p className="upload-card__hint">需要授权其他浏览器时，请在手机 App 的“设置”中扫描新绑定码。</p>
        </article>
      </section>

      <section className="history-card">
        <div className="section-heading"><div><p className="eyebrow">留痕</p><h2>二维码历史</h2></div><span>{versions.length} 个版本</span></div>
        {versions.length === 0 ? (
          <div className="history-empty"><History size={22} /><span>手机自动发现并启用群码后，会在这里保留版本记录。</span></div>
        ) : (
          <div className="history-list">
            {versions.map((version, index) => (
              <div className="history-row" key={version.id}>
                <span className="history-row__index">{String(versions.length - index).padStart(2, "0")}</span>
                <div><strong>{version.id === channel.activeQrVersionId ? "当前启用版本" : "历史版本"}</strong><small>{formatDateTime(version.activatedAt)}</small></div>
                <code>{version.decodedPayloadHash.slice(0, 12)}…</code>
                {version.id === channel.activeQrVersionId ? <span className="status-badge status-badge--success"><i />使用中</span> : null}
              </div>
            ))}
          </div>
        )}
      </section>

      <section className="availability-card">
        <div>
          <strong><Power size={16} /> 频道入口</strong>
          <p>停用后分享链接和当前入群码会暂时不可访问；重新启用即可恢复。</p>
        </div>
        <label className="switch-control">
          <input
            aria-label="启用频道"
            checked={!channel.disabledAt}
            disabled={updatingEnabled}
            role="switch"
            type="checkbox"
            onChange={(event) => void setChannelEnabled(event.target.checked)}
          />
          <span aria-hidden="true" />
          <b>{updatingEnabled ? "正在更新…" : channel.disabledAt ? "已停用" : "已启用"}</b>
        </label>
      </section>

      {actionError ? <p className="form-error channel-action-error" role="alert">{actionError}</p> : null}

      <section className="danger-zone">
        <div><strong>永久删除频道</strong><p>频道、公开地址与版本历史会被永久删除，无法恢复。</p></div>
        <button className="button button--danger button--small" type="button" onClick={() => void deleteChannel()}><Trash2 size={15} /> 永久删除</button>
      </section>
    </div>
  );
}
